import { apiClient } from './client';
import type {
  CommuteAddresses,
  CreateMyExpenseClaim,
  EmployeeCategoryResult,
  ExpenseAttachment,
  ExpenseClaim,
  ExpenseType,
  MobilityAccount,
  MobilityLedgerEntry,
  MobilitySelfProfile,
  PillarThreeProjection,
} from '@/types/mobility';

/** Employee self-service endpoints (the "me" surface of the mobility module). */
export const mobilitySelfApi = {
  /**
   * Fetches the signed-in user's mobility profile (whether they are linked
   * to an employee record and have mobility budget enabled).
   *
   * @returns The caller's self-service profile.
   */
  async getProfile(): Promise<MobilitySelfProfile> {
    const { data } = await apiClient.get<MobilitySelfProfile>('/api/mobility/me');
    return data;
  },

  /**
   * Fetches the caller's mobility account for a given year.
   *
   * @param year - The calendar year to fetch the account for.
   * @returns The mobility account for that year.
   */
  async getMyAccount(year: number): Promise<MobilityAccount> {
    const { data } = await apiClient.get<MobilityAccount>(`/api/mobility/me/accounts/${year}`);
    return data;
  },

  /**
   * Fetches the caller's mobility ledger entries for a given year.
   *
   * @param year - The calendar year to fetch ledger entries for.
   * @returns The ledger entries for that year.
   */
  async getMyLedger(year: number): Promise<MobilityLedgerEntry[]> {
    const { data } = await apiClient.get<MobilityLedgerEntry[]>(
      `/api/mobility/me/accounts/${year}/ledger`,
    );
    return data;
  },

  /**
   * Fetches the caller's Pillar 3 (cash payout) contribution projection for a given year.
   *
   * @param year - The calendar year to project.
   * @returns The Pillar 3 projection for that year.
   */
  async getMyPillarThreeProjection(year: number): Promise<PillarThreeProjection> {
    const { data } = await apiClient.get<PillarThreeProjection>(
      `/api/mobility/me/accounts/${year}/pillar-three-projection`,
    );
    return data;
  },

  /**
   * Fetches the caller's current mobility category eligibility result.
   *
   * @returns The caller's category calculation result.
   */
  async getMyCategory(): Promise<EmployeeCategoryResult> {
    const { data } = await apiClient.get<EmployeeCategoryResult>('/api/mobility/me/category');
    return data;
  },

  /**
   * Fetches the catalog of expense types available for self-service claims.
   *
   * @returns The list of available expense types.
   */
  async getExpenseTypes(): Promise<ExpenseType[]> {
    const { data } = await apiClient.get<ExpenseType[]>('/api/mobility/me/expense-types');
    return data;
  },

  /**
   * Fetches the home and work address of the caller's most recent woon-werk
   * claim, used to prefill a new woon-werk claim.
   *
   * @returns The last used addresses (null fields when there is none yet).
   */
  async getMyCommuteAddresses(): Promise<CommuteAddresses> {
    const { data } = await apiClient.get<CommuteAddresses>('/api/mobility/me/expense-claims/commute-addresses');
    return data;
  },

  /**
   * Fetches the caller's expense claims for a given year.
   *
   * @param year - The calendar year to fetch claims for.
   * @returns The caller's claims for that year.
   */
  async getMyClaims(year: number): Promise<ExpenseClaim[]> {
    const { data } = await apiClient.get<ExpenseClaim[]>('/api/mobility/me/expense-claims', {
      params: { year },
    });
    return data;
  },

  /**
   * Creates a new expense claim (as a draft) for the caller.
   *
   * @param payload - The claim details to submit.
   * @returns The created claim.
   */
  async createMyClaim(payload: CreateMyExpenseClaim): Promise<ExpenseClaim> {
    const { data } = await apiClient.post<ExpenseClaim>('/api/mobility/me/expense-claims', payload);
    return data;
  },

  /**
   * Uploads a supporting attachment for one of the caller's claims.
   *
   * @param claimId - The id of the claim to attach the file to.
   * @param file - The file to upload.
   * @returns The stored attachment's metadata.
   */
  async uploadMyAttachment(claimId: number, file: File): Promise<ExpenseAttachment> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<ExpenseAttachment>(
      `/api/mobility/me/expense-claims/${claimId}/attachments`,
      form,
    );
    return data;
  },

  /**
   * Downloads a claim attachment and triggers a browser save, using a
   * temporary object URL that is revoked immediately after the click.
   *
   * @param claimId - The id of the claim the attachment belongs to.
   * @param attachment - The attachment to download (used for its id and file name).
   */
  async downloadMyAttachment(claimId: number, attachment: ExpenseAttachment): Promise<void> {
    const { data } = await apiClient.get<Blob>(
      `/api/mobility/me/expense-claims/${claimId}/attachments/${attachment.id}/content`,
      { responseType: 'blob' },
    );
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Resubmits a rejected claim with revised details.
   *
   * @param claimId - The id of the rejected claim to revise.
   * @param payload - The updated claim details.
   * @returns The revised claim.
   */
  async reviseMyRejectedClaim(claimId: number, payload: CreateMyExpenseClaim): Promise<ExpenseClaim> {
    const { data } = await apiClient.put<ExpenseClaim>(
      `/api/mobility/me/expense-claims/${claimId}/revise`,
      payload,
    );
    return data;
  },

  /**
   * Submits a draft claim for approval.
   *
   * @param claimId - The id of the draft claim to submit.
   * @returns The submitted claim.
   */
  async submitMyClaim(claimId: number): Promise<ExpenseClaim> {
    const { data } = await apiClient.post<ExpenseClaim>(
      `/api/mobility/me/expense-claims/${claimId}/submit`,
    );
    return data;
  },

  /**
   * Cancels one of the caller's claims.
   *
   * @param claimId - The id of the claim to cancel.
   * @returns The cancelled claim.
   */
  async cancelMyClaim(claimId: number): Promise<ExpenseClaim> {
    const { data } = await apiClient.post<ExpenseClaim>(
      `/api/mobility/me/expense-claims/${claimId}/cancel`,
    );
    return data;
  },

  /**
   * Opts the caller into the yearly (lump-sum) budget release mode for a given year,
   * instead of the default monthly release.
   *
   * @param year - The calendar year to select yearly release for.
   * @returns The updated mobility account.
   */
  async selectYearlyRelease(year: number): Promise<MobilityAccount> {
    const { data } = await apiClient.post<MobilityAccount>(
      `/api/mobility/me/accounts/${year}/select-yearly-release`,
    );
    return data;
  },
};
